import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Globe, Users, Bot, LifeBuoy, Calendar, Mail, Search, BarChart3,
  CreditCard, Wrench, ArrowRight, ChevronDown, Zap, Sparkles, ShieldCheck,
  ArrowUpRight, Cpu, Terminal, Layers, CheckCircle2, Radar, Rocket, TrendingUp,
  Star, Award, Send,
} from "lucide-react";
import { BRAND, SHOWCASE } from "@/config/brand";
import { DFW_CITIES } from "@/config/cities";
import PlugitAIChat from "@/pages/PlugitAIChat";
import AITwinSlideshow from "@/pages/AITwinSlideshow";
import FounderLetter from "@/pages/FounderLetter";
import TestimonialsWall from "@/pages/TestimonialsWall";
import KitOnTV from "@/pages/KitOnTV";
import HowWeWork from "@/pages/HowWeWork";
import IsWebsiteRightForYou from "@/pages/IsWebsiteRightForYou";
import MissRodriguezCaseStudy from "@/pages/MissRodriguezCaseStudy";
import MobilePocketPopup from "@/pages/MobilePocketPopup";
import QAndA from "@/pages/QAndA";
import PriorityMarketsNav from "@/pages/PriorityMarketsNav";

// ─────────────────────────────────────────────────────────────────────
// PlugitAI marketing landing — dark canvas · blue + green · edge-to-edge
// ─────────────────────────────────────────────────────────────────────

const C = BRAND.colors;

const MODULES = [
  { icon: Globe,      title: "Websites",             body: "Full CMS, block editor, hosted publishing. 25 components, 17 industry presets." },
  { icon: Users,      title: "CRM & Leads",          body: "Every call, form, and chat pipes into one inbox with full contact history." },
  { icon: Bot,        title: "SalesBot",             body: "24/7 consultative AI that qualifies, quotes, and closes right in your site's chat widget." },
  { icon: LifeBuoy,   title: "Support Bot",          body: "Trained on your policies + FAQs. Answers customers the same way at 3 AM as you would at noon." },
  { icon: Calendar,   title: "Scheduling",           body: "Two-way calendar sync, self-serve booking, SMS + email reminders." },
  { icon: Mail,       title: "Email + SMS Automation", body: "Trigger-based follow-up sequences across email and SMS. No Zapier tax." },
  { icon: Search,     title: "SEO Engine",           body: "JSON-LD, sitemaps, hreflang, canonicals, Open Graph — auto-injected on every page." },
  { icon: BarChart3,  title: "Analytics",            body: "Traffic, conversions, chat outcomes, revenue attribution — one glass." },
  { icon: CreditCard, title: "Payments + Billing",   body: "Stripe subscriptions, one-time checkouts, invoicing. Ready when you switch it on." },
  { icon: Wrench,     title: "Industry Tools",       body: "Vertical-specific calculators, wizards, PDF exports. Custom-built per client." },
];

const STATS = [
  { value: "15,455", label: "Live pages this Summer" },
  { value: "266+",   label: "Client pages live" },
  { value: "24/7",   label: "AI conversations live" },
  { value: "$0",     label: "Zapier / SaaS lock-in" },
];

const HOW = [
  { n: "01", title: "Pick your modules",  body: "Turn on Websites, CRM, SalesBot, Scheduling — whatever your business actually needs." },
  { n: "02", title: "We wire it in",       body: "One database, one login, your brand. No duct-taped SaaS. Live in days, not weeks." },
  { n: "03", title: "Ship. Sell. Scale.",  body: "Every module talks to every other module. Add clients, verticals, or geographies without rebuilding." },
];

// ── Zero-friction hero lead capture ─────────────────────────────────────
// Two visible fields (name + email) → POSTs to the existing /api/public/
// onboard-request endpoint with sensible defaults, so a hero visitor can be
// an inbox lead in 5 seconds without touching a full modal.
function HeroLeadForm() {
  const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setState("sending");
    try {
      await axios.post(`${API}/public/onboard-request`, {
        contact_name: name.trim(),
        email: email.trim(),
        phone: "",
        company_name: name.trim() + " (from hero)",
        industry: "Website plan request",
        website: "",
        primary_color: "",
        modules: ["website"],
        monthly_budget: "",
        launch_target: "",
        notes: "Requested a free Dallas website plan from the homepage hero.",
      });
      setState("sent");
      // GA / PostHog signal (no-op if not present)
      try { window.gtag && window.gtag("event", "lead_hero_form", { source: "hero" }); } catch (_) {}
    } catch (err) {
      setState("error");
    }
  };
  if (state === "sent") {
    return (
      <div
        className="mt-6 max-w-[560px] rounded-xl p-4 border flex items-start gap-3"
        style={{ background: "rgba(34,197,94,0.10)", borderColor: "rgba(34,197,94,0.55)" }}
        data-testid="hero-form-sent"
      >
        <CheckCircle2 size={20} className="text-emerald-400 shrink-0 mt-0.5" />
        <div>
          <div className="font-black text-white">Got it. Kit will call you within 24 hours.</div>
          <div className="text-[13px] text-white/70 mt-1">
            You&#39;ll get a free 15-min consult + a same-week build plan for your Dallas business. No commitment.
          </div>
        </div>
      </div>
    );
  }
  return (
    <form
      onSubmit={submit}
      className="mt-6 max-w-[560px]"
      data-testid="hero-lead-form"
    >
      <div className="text-[10px] uppercase tracking-[0.28em] font-black text-white/50 mb-2">
        Or grab a free 15-min consult →
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="h-12 flex-1 px-4 rounded-md bg-white/[0.04] border border-white/15 text-white placeholder-white/40 focus:border-blue-400 focus:bg-white/[0.06] outline-none text-sm transition-colors"
          data-testid="hero-form-name"
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          className="h-12 flex-1 px-4 rounded-md bg-white/[0.04] border border-white/15 text-white placeholder-white/40 focus:border-blue-400 focus:bg-white/[0.06] outline-none text-sm transition-colors"
          data-testid="hero-form-email"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          className="h-12 px-6 rounded-md font-black text-sm uppercase tracking-[0.16em] bg-white text-slate-900 hover:bg-slate-100 disabled:opacity-60 transition-colors inline-flex items-center gap-2"
          data-testid="hero-form-submit"
        >
          {state === "sending" ? "Sending…" : (<>Get Plan <Send size={14} /></>)}
        </button>
      </div>
      {state === "error" && (
        <div className="mt-2 text-[12px] text-red-400" data-testid="hero-form-error">
          Something went wrong. Call Kit directly: (214) 815-5966.
        </div>
      )}
      <div className="mt-2 text-[11px] text-white/45">
        No spam · No commitment · Same-week launch from $299.99
      </div>
    </form>
  );
}

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    // Homepage SEO — Dallas-first commercial positioning (Aug 2026 SEO rebuild).
    document.title = "Dallas Website Design + AI Sales Agents | PlugitAI";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(
      "description",
      "PlugitAI builds high-performance Dallas websites with AI sales agents, local SEO, and business automation baked in. Fort Worth · Plano · Frisco · DFW. Same-week launch from $299.99. Founded by Chris (Kit) Garcia — 1 of 4 certified Google Experts in Texas."
    );
    setMeta("robots", "index,follow,max-image-preview:large,max-snippet:-1");

    // Open Graph + Twitter
    const setProp = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const origin = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://plugitai.com");
    setProp("og:title", "Dallas Website Design + AI Sales Agents | PlugitAI");
    setProp("og:description", "High-performance Dallas websites with AI sales agents, local SEO, and business automation. Same-week launch from $299.99. DFW-based, built by Kit Garcia.");
    setProp("og:type", "website");
    setProp("og:url", `${origin}/`);
    setProp("og:image", `${origin}/plugitai-og.png`);
    setMeta("twitter:card", "summary_large_image");

    // Canonical
    let canon = document.querySelector("link[rel='canonical']");
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = `${origin}/`;

    // JSON-LD: Organization + WebSite + BreadcrumbList
    const upsertLD = (id, data) => {
      let el = document.getElementById(id);
      if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
      el.textContent = JSON.stringify(data);
    };
    upsertLD("ld-home-org", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "PlugitAI",
      alternateName: "Nariai Core",
      url: origin,
      logo: `${origin}${BRAND.logos.plugitai}`,
      telephone: "+12148155966",
      email: "cgarciathanksyou@gmail.com",
      areaServed: { "@type": "AdministrativeArea", name: "Dallas–Fort Worth, TX" },
      founder: { "@type": "Person", name: "Chris Garcia", alternateName: "Kit" },
      sameAs: [],
    });
    // LocalBusiness — critical for local pack rankings + Google Business Profile alignment
    upsertLD("ld-home-localbusiness", {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "@id": `${origin}/#localbusiness`,
      name: "PlugitAI",
      image: `${origin}${BRAND.logos.plugitai}`,
      url: origin,
      telephone: "+1-214-815-5966",
      email: "cgarciathanksyou@gmail.com",
      priceRange: "$600–$2000",
      description:
        "PlugitAI is a small, in-house web-design and AI-sales-agent studio serving small businesses across the Dallas–Fort Worth metroplex. Websites from $600–$2,000, 100 pages of exclusive content, and a 24/7 AI receptionist on every build.",
      address: {
        "@type": "PostalAddress",
        streetAddress: "2401 S Stemmons Frwy, Suite 1321",
        addressLocality: "Lewisville",
        addressRegion: "TX",
        postalCode: "75067",
        addressCountry: "US",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 33.0071,
        longitude: -96.9803,
      },
      areaServed: [
        { "@type": "City", name: "Dallas" },
        { "@type": "City", name: "Fort Worth" },
        { "@type": "City", name: "Plano" },
        { "@type": "City", name: "Frisco" },
        { "@type": "City", name: "McKinney" },
        { "@type": "City", name: "Arlington" },
        { "@type": "City", name: "Irving" },
        { "@type": "City", name: "Lewisville" },
        { "@type": "AdministrativeArea", name: "Dallas–Fort Worth Metroplex" },
      ],
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "08:00",
          closes: "19:00",
        },
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Saturday"],
          opens: "10:00",
          closes: "16:00",
        },
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "PlugitAI services",
        itemListElement: [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Custom website design + build" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Local SEO + Google indexing" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "24/7 AI sales-agent chat widget" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "CRM + lead capture + automated follow-up" } },
        ],
      },
      founder: { "@type": "Person", name: "Chris Garcia", alternateName: "Kit" },
    });
    // FAQPage — homepage-level FAQ so Google can pull rich results for the money queries
    upsertLD("ld-home-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How much does a PlugitAI website cost?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Our average web-design projects run $600 to $2,000, and we have payment options. Every build includes 100 pages of exclusive content, a 24/7 AI chatbot, local SEO across every city you serve, review management, and lead capture — no hidden monthly SEO retainer.",
          },
        },
        {
          "@type": "Question",
          name: "How long does it take to build a website?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Most PlugitAI sites launch within 5–14 days once we have your brand assets and content sign-off. Our in-house team of four handles design, development, copy, and SEO in parallel so nothing waits on a hand-off.",
          },
        },
        {
          "@type": "Question",
          name: "Do you offer SEO?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes — SEO is Kit's hobby, so it's baked into every build instead of billed as a padded monthly line-item. Every site ships with schema markup, an XML sitemap, optimized metadata, Core Web Vitals tuning, Local Business optimization, and dedicated city + service pages for every market you serve.",
          },
        },
        {
          "@type": "Question",
          name: "Which cities do you serve?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "PlugitAI is based in Lewisville, TX and serves the entire Dallas–Fort Worth metroplex including Dallas, Fort Worth, Plano, Frisco, McKinney, Arlington, Irving, Garland, Grand Prairie, Denton, and 60+ surrounding cities and counties.",
          },
        },
        {
          "@type": "Question",
          name: "What makes PlugitAI different from other agencies?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Most agencies build 10–20 pages and call it a website. PlugitAI builds complete 250–400-page digital ecosystems designed to generate leads 24/7 through a live AI receptionist, city-level SEO coverage, and instant lead capture — all in-house, no overseas subcontracting, and no ongoing hostage-fee model.",
          },
        },
      ],
    });
    upsertLD("ld-home-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "PlugitAI",
      url: origin,
      potentialAction: {
        "@type": "SearchAction",
        target: `${origin}/tx/{search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });
    upsertLD("ld-home-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin },
      ],
    });

    // brand-forward favicon
    const link = document.querySelector("link[rel='icon']");
    if (link) link.href = BRAND.logos.plugitaiFavicon;

    return () => {
      ["ld-home-org", "ld-home-localbusiness", "ld-home-faq", "ld-home-website", "ld-home-breadcrumb"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div
      className="min-h-screen w-full overflow-x-hidden pb-24 md:pb-0"
      style={{ background: C.ink, color: C.text, fontFamily: "'Inter', 'Cabinet Grotesk', system-ui, sans-serif" }}
      data-testid="plugitai-landing"
    >
      <AmbientGrid />
      <TopNav scrolled={scrolled} />
      <Hero />
      <IsWebsiteRightForYou />
      <KitOnTV />
      <HowWeWork />
      <MissRodriguezCaseStudy />
      <FounderLetter />
      <TrustStrip />
      <Showcase />
      <TestimonialsWall />
      <Pricing />
      <PriorityMarketsNav />
      <QAndA />
      <Modules />
      <HowItWorks />
      <FinalCTA />
      <Footer />
      <PlugitAIChat />
      <MobilePocketPopup />
    </div>
  );
}

// ────────── Ambient background: grid + soft gradients ──────────
function AmbientGrid() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: `radial-gradient(1200px 700px at 15% -10%, ${C.blueBright}22, transparent 60%),
                       radial-gradient(900px 600px at 90% 15%, ${C.greenBright}18, transparent 60%),
                       radial-gradient(1400px 900px at 60% 110%, ${C.blueDeep}44, transparent 70%)`,
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.25]"
        style={{
          backgroundImage: `linear-gradient(${C.line} 1px, transparent 1px),
                            linear-gradient(90deg, ${C.line} 1px, transparent 1px)`,
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 80%)",
        }}
      />
    </>
  );
}

// ────────── Top nav ──────────
function TopNav({ scrolled }) {
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(10,15,30,0.72)" : "transparent",
        backdropFilter: scrolled ? "blur(14px) saturate(140%)" : "none",
        borderBottom: scrolled ? `1px solid ${C.line}` : "1px solid transparent",
      }}
      data-testid="plugitai-topnav"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="plugitai-brand">
          <img
            src={BRAND.logos.plugitai}
            alt="PlugitAI"
            className="h-10 md:h-12 w-auto drop-shadow-[0_0_18px_rgba(59,130,246,0.35)]"
          />
          <span className="hidden xl:inline-block whitespace-nowrap text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-5 lg:gap-7 text-sm whitespace-nowrap" style={{ color: C.textDim }}>
          <a href="#showcase"      className="hover:text-white transition-colors" data-testid="nav-showcase">Showcase</a>
          <a href="#testimonials"  className="hidden xl:inline hover:text-white transition-colors" data-testid="nav-testimonials">Testimonials</a>
          <a href="#pricing"       className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
          <Link to="/about"        className="hover:text-white transition-colors" data-testid="nav-about">About Kit</Link>
          <Link to="/command-center" className="hidden xl:inline hover:text-white transition-colors" data-testid="nav-command">Command Center</Link>
          <Link to="/tx"           className="hover:text-white transition-colors" data-testid="nav-cities">Cities</Link>
          <Link to="/learn"        className="hover:text-white transition-colors" data-testid="nav-learn">Learn</Link>
          <Link to="/blog"         className="hover:text-white transition-colors" data-testid="nav-blog">Blog</Link>
          <Link to="/bots"         className="font-black uppercase tracking-[0.18em] text-[11px] px-3 py-1 rounded-md" style={{ color: "#EF4444", background: "rgba(220,38,38,0.10)", border: "1px solid rgba(220,38,38,0.45)" }} data-testid="nav-free-bot">Free Bot</Link>
          <Link to="/custom"       className="font-black uppercase tracking-[0.18em] text-[11px] px-3 py-1 rounded-md" style={{ color: "#4CC3FF", background: "rgba(0,163,255,0.10)", border: "1px solid rgba(0,163,255,0.45)" }} data-testid="nav-custom">Custom</Link>
          <Link to="/clients"      className="font-black uppercase tracking-[0.18em] text-[11px] px-3 py-1 rounded-md" style={{ color: "#22C55E", background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.45)" }} data-testid="nav-clients">Clients</Link>
          <Link to="/login"        className="hidden xl:inline hover:text-white transition-colors" data-testid="nav-signin">Sign in</Link>
        </nav>

        <a
          href="#contact"
          className="ml-6 inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-12px_rgba(34,197,94,0.6)] shrink-0 whitespace-nowrap"
          style={{
            background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
            color: "white",
          }}
          data-testid="nav-cta-book"
        >
          Book a build <ArrowRight size={14} />
        </a>
      </div>
    </header>
  );
}

// ────────── Hero — AI Twin ──────────
function Hero() {
  return (
    <section className="relative z-10 pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden" data-testid="hero-section">
      {/* Feb 2026 mobile-perf: killed the 8MB hero video (was blocking LCP at
          107s on 4G). Poster image + gradient wash delivers the same "premium
          motion" feel via CSS-only ambient glows — zero decode, zero JS. */}
      <div aria-hidden className="absolute inset-0 z-0">
        <img
          src="/videos/hero-poster.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
          loading="eager"
          fetchPriority="low"
          decoding="async"
          data-testid="hero-bg-poster"
        />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg, rgba(10,15,30,0.72) 0%, rgba(10,15,30,0.85) 55%, ${C.ink} 100%),
                         radial-gradient(1000px 600px at 10% 20%, ${C.violet}22, transparent 60%),
                         radial-gradient(900px 500px at 90% 30%, ${C.amber}18, transparent 60%)`,
          }}
        />
        {/* Grain overlay — adds a premium film-grain texture over the wash.
            Uses an inline SVG so no extra network request. */}
        <div
          className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.7 0'/></filter><rect width='200' height='200' filter='url(%23n)'/></svg>\")",
            backgroundSize: "200px 200px",
          }}
          aria-hidden
        />
      </div>

      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-10 lg:gap-14 items-center">
          {/* LEFT — copy */}
          <div className="order-2 lg:order-1">
            {/* Trust bar — 4 real credentials, above everything else. This is
                the single strongest "why should I trust you" element on the
                page. Keep the copy factual (Kit is a Top-10 SEO Expert 2026,
                1 of 4 certified TX Google Experts, 4.9★ on Google reviews). */}
            <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="hero-trust-bar">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] md:text-[11px] font-black uppercase tracking-[0.14em] border"
                    style={{ background: "rgba(255,215,0,0.10)", borderColor: "rgba(255,215,0,0.45)", color: "#FFD700" }}>
                <Star size={11} fill="#FFD700" strokeWidth={0} /> 4.9 / 5 Google
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] md:text-[11px] font-black uppercase tracking-[0.14em] border"
                    style={{ background: "rgba(59,130,246,0.10)", borderColor: `${C.blueBright}66`, color: C.blueGlow }}>
                <Award size={11} /> Top-10 SEO Expert · 2026
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] md:text-[11px] font-black uppercase tracking-[0.14em] border"
                    style={{ background: "rgba(34,197,94,0.10)", borderColor: `${C.greenBright}66`, color: C.greenGlow }}>
                <ShieldCheck size={11} /> 1 of 4 TX Google Experts
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] md:text-[11px] font-black uppercase tracking-[0.14em] border border-white/20 text-white/70">
                <TrendingUp size={11} /> 30+ DFW businesses live
              </span>
            </div>

            {/* Eyebrow pill */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-[0.2em] mb-8 animate-pulse"
              style={{
                background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
                color: "white",
                boxShadow: `0 12px 32px -10px ${C.blueBright}88`,
              }}
              data-testid="hero-urgency-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white" />
              It gets smarter by the day
            </div>

            {/* SEO eyebrow — geographic + commercial signal for crawlers + first-glance clarity */}
            <div className="text-[11px] md:text-xs font-black uppercase tracking-[0.28em] mb-3" style={{ color: C.greenGlow }} data-testid="hero-eyebrow-dallas">
              Dallas · Fort Worth · DFW · serving all of Texas
            </div>

            {/* Headline — Dallas-first + AI-first commercial positioning (Aug 2026 SEO rebuild) */}
            <h1
              className="font-black tracking-[-0.02em] leading-[0.98]"
              style={{ fontSize: "clamp(2.4rem, 5.4vw, 5.4rem)" }}
              data-testid="hero-headline"
            >
              Dallas Website Design +{" "}
              <span
                className="inline-block relative"
                style={{
                  backgroundImage: `linear-gradient(135deg, ${C.amber} 0%, #F59E0B 45%, ${C.violetSoft} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  filter: `drop-shadow(0 0 24px rgba(252,211,77,0.48))`,
                }}
              >
                an AI
              </span>{" "}
              That Books Your Next Customer While You Sleep.
            </h1>

            {/* Subhead — one clean benefit + trust + price, no filler */}
            <p className="mt-7 max-w-[640px] text-lg md:text-xl leading-relaxed" style={{ color: C.textDim }} data-testid="hero-subhead">
              Same-week Dallas website with an <span className="text-white font-semibold">AI sales agent baked in</span> — it answers chats, qualifies leads, and books appointments while you&#39;re on the ladder. <span className="text-white font-semibold">Launches from $299.99.</span> Built in DFW by Kit Garcia.
            </p>

            {/* Twin capability chips */}
            <div className="mt-6 flex flex-wrap gap-2 max-w-[620px]" data-testid="hero-twin-capabilities">
              {["Chats with customers", "Answers emails", "Books appointments", "Takes payments", "Updates your CRM", "Runs follow-ups"].map((cap) => (
                <span
                  key={cap}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border"
                  style={{
                    background: "rgba(59,130,246,0.10)",
                    borderColor: `${C.blueBright}55`,
                    color: C.blueGlow,
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <span className="w-1 h-1 rounded-full" style={{ background: C.greenGlow, boxShadow: `0 0 6px ${C.greenGlow}` }} />
                  {cap}
                </span>
              ))}
            </div>

            {/* CTAs — action-oriented copy + risk-reversal microcopy below */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#pricing"
                className="inline-flex items-center gap-2 px-6 md:px-8 h-14 rounded-full font-semibold text-base shadow-2xl transition-all hover:-translate-y-0.5"
                style={{
                  background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
                  boxShadow: `0 20px 60px -20px ${C.greenBright}88`,
                  color: "white",
                }}
                data-testid="hero-cta-primary"
              >
                See what a $299 Dallas site looks like <ArrowRight size={16} />
              </a>
              <a
                href="tel:+12148155966"
                className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border transition-all hover:bg-white/5"
                style={{ borderColor: `${C.blueBright}66`, color: C.text }}
                data-testid="hero-cta-secondary"
              >
                Call Kit: (214) 815-5966 <ArrowUpRight size={16} />
              </a>
            </div>

            {/* Risk-reversal microcopy — kills the "what's the catch" objection */}
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]" style={{ color: C.textDim }} data-testid="hero-risk-reversal">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-emerald-400" /> Free 15-min plan
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-emerald-400" /> No commitment
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-emerald-400" /> Same-week launch
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-emerald-400" /> DFW-based
              </span>
            </div>

            {/* Inline zero-friction lead form → POSTs to /api/public/onboard-request */}
            <HeroLeadForm />

            {/* Returning-client callout */}
            <a
              href="mailto:cgarciathanksyou@gmail.com?subject=Returning%20client%20rate"
              className="mt-8 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border transition-all hover:-translate-y-0.5"
              style={{
                background: `${C.blueBright}12`,
                borderColor: `${C.blueBright}66`,
                color: C.blueGlow,
                backdropFilter: "blur(8px)",
              }}
              data-testid="hero-returning-client"
            >
              <Sparkles size={14} />
              Returning client? Email me for your rate.
              <ArrowUpRight size={14} />
            </a>

            {/* Client social-proof strip — real live tenants Kit has shipped for.
                Text-only wordmarks keep it fast (no logo image fetches) and
                honest (no invented brands). Renders in a subtle single line. */}
            <div className="mt-8 pt-6 border-t border-white/10" data-testid="hero-social-proof">
              <div className="text-[10px] uppercase tracking-[0.32em] font-black text-white/40 mb-3">
                Trusted by
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] font-black tracking-tight text-white/70">
                <span>Lone Star Striping Pros</span>
                <span className="text-white/20">·</span>
                <span>Miss Rodriguez Pest</span>
                <span className="text-white/20">·</span>
                <span>Lone Star Lumpers</span>
                <span className="text-white/20">·</span>
                <span>iHeartFrenchies</span>
                <span className="text-white/20">·</span>
                <span>LeenRx</span>
                <span className="text-white/20">·</span>
                <span>CargoPros</span>
              </div>
            </div>
          </div>

          {/* RIGHT — AI Twin visual slideshow (rotates through 20+ diverse owners) */}
          <div className="order-1 lg:order-2 relative" data-testid="hero-twin-visual">
            <AITwinSlideshow />
          </div>
        </div>

        {/* "Your company needs a bot — FREE" hook — replaces the old numbers strip */}
        <div className="mt-14 md:mt-16 max-w-[720px]">
          <Link
            to="/bots"
            className="group relative flex items-center gap-4 md:gap-5 rounded-2xl p-4 md:p-5 border overflow-hidden transition-transform hover:-translate-y-0.5"
            style={{
              background:
                "linear-gradient(135deg, rgba(220,38,38,0.14) 0%, rgba(11,21,36,0.85) 55%, rgba(0,0,0,0.9) 100%)",
              borderColor: "rgba(220,38,38,0.55)",
              boxShadow:
                "0 30px 80px -30px rgba(220,38,38,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
            }}
            data-testid="hero-free-bot-cta"
          >
            {/* Ambient scan-line glow behind */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-70"
              style={{
                background:
                  "radial-gradient(300px 100px at 20% 50%, rgba(239,68,68,0.35), transparent 70%)",
              }}
            />
            {/* Icon capsule */}
            <div
              className="relative shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #DC2626 0%, #7F1D1D 100%)",
                boxShadow: "0 12px 30px -10px rgba(220,38,38,0.85)",
              }}
            >
              <Bot size={22} color="white" />
            </div>

            <div className="relative min-w-0 flex-1">
              <div
                className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.28em]"
                style={{ color: "#FCA5A5" }}
              >
                Your company needs a bot
              </div>
              <div
                className="mt-1 font-black text-white leading-tight"
                style={{ fontSize: "clamp(1.15rem, 2vw, 1.5rem)" }}
              >
                Get one <span style={{ color: "#EF4444" }}>free</span>. Change your business by tomorrow.
              </div>
              <div className="mt-1 text-xs md:text-sm" style={{ color: C.textDim }}>
                Chat bot · Sales bot · Inventory bot — pick your poison. See the black-and-red bot page.
              </div>
            </div>

            <div
              className="relative shrink-0 hidden sm:inline-flex items-center gap-1.5 px-3.5 h-10 rounded-full text-[11px] font-black uppercase tracking-[0.22em]"
              style={{
                background: "#DC2626",
                color: "white",
                boxShadow: "0 12px 30px -10px rgba(220,38,38,0.75)",
              }}
            >
              Free bot <ArrowUpRight size={13} />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ────────── Trust strip ──────────
function TrustStrip() {
  const items = [
    { icon: Cpu,        label: `Powered by ${BRAND.engineName}`, gold: true },
    { icon: Bot,        label: `${BRAND.botName} inside` },
    { icon: ShieldCheck, label: "6-tier RBAC · Multi-tenant secure" },
    { icon: Terminal,   label: "React · FastAPI · Mongo" },
    { icon: Zap,        label: "Live in days · not weeks" },
  ];
  return (
    <section className="relative z-10 border-y" style={{ borderColor: C.line, background: "rgba(17,24,39,0.55)" }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
        {items.map(({ icon: Ico, label, gold }) => (
          <div key={label} className={`flex items-center gap-2 text-xs uppercase tracking-[0.18em] ${gold ? "gold-shine gold-shine-dark" : ""}`} style={gold ? undefined : { color: C.textDim }}>
            <Ico size={14} style={{ color: gold ? "#FFD700" : C.greenGlow }} />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ────────── Showcase — This month's builds (all live clients) ──────────
function Showcase() {
  return (
    <section id="showcase" className="relative z-10 pt-28 md:pt-36 pb-20" data-testid="showcase-section">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        {/* Header */}
        <div className="max-w-[900px] mb-14">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: C.greenGlow }}>
            This month&apos;s builds
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02]"
              style={{ fontSize: "clamp(2rem, 4.4vw, 3.8rem)" }}>
            Not your SEO company.{" "}
            <span
              style={{
                backgroundImage: `linear-gradient(135deg, ${C.blueGlow} 0%, ${C.greenGlow} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Home of bots + stealth bots.
            </span>
          </h2>
          <p className="mt-4 text-lg" style={{ color: C.textDim }}>
            Every site below was designed, built, wired, and launched by {BRAND.platformName} this
            month. Live traffic, live AI chat, live lead-gen crawlers running in the background —
            not slide decks, not mockups. Real revenue engines.
          </p>
        </div>

        {/* Peek-inside CTA — lets prospects tour the live client dashboard
            (data blurred) before they even hit pricing. Sits above the client
            grid because it's the highest-intent proof element on the page. */}
        <Link
          to="/preview"
          className="group relative flex items-center gap-4 md:gap-5 rounded-2xl p-4 md:p-5 mb-10 md:mb-12 border overflow-hidden transition-transform hover:-translate-y-0.5"
          style={{
            background: `linear-gradient(135deg, rgba(139,92,246,0.20) 0%, rgba(11,21,36,0.88) 55%, rgba(252,211,77,0.14) 100%)`,
            borderColor: `${C.violetSoft}66`,
            boxShadow: `0 30px 80px -30px ${C.violet}66, inset 0 1px 0 rgba(255,255,255,0.05)`,
          }}
          data-testid="showcase-peek-inside-cta"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-70"
            style={{
              background: `radial-gradient(320px 120px at 18% 50%, ${C.violet}44, transparent 70%),
                           radial-gradient(320px 120px at 82% 50%, ${C.amber}33, transparent 70%)`,
            }}
          />
          <div
            className="relative shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${C.violet} 0%, ${C.amber} 100%)`,
              boxShadow: `0 12px 30px -10px ${C.violet}aa`,
            }}
          >
            <BarChart3 size={22} color="white" />
          </div>
          <div className="relative min-w-0 flex-1">
            <div
              className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.28em]"
              style={{ color: C.amber }}
            >
              Peek inside · Live client dashboard
            </div>
            <div
              className="mt-1 font-black text-white leading-tight"
              style={{ fontSize: "clamp(1.15rem, 2vw, 1.5rem)" }}
            >
              See what your <span style={{ color: C.violetSoft }}>logged-in view</span> looks like — data blurred for privacy.
            </div>
            <div className="mt-1 text-xs md:text-sm" style={{ color: C.textDim }}>
              Leads, chats, pipeline, payments, AI activity — all in one glass. Every build ships with it.
            </div>
          </div>
          <div
            className="relative shrink-0 hidden sm:inline-flex items-center gap-1.5 px-3.5 h-10 rounded-full text-[11px] font-black uppercase tracking-[0.22em] text-white"
            style={{
              background: `linear-gradient(135deg, ${C.violet} 0%, ${C.amber} 100%)`,
              boxShadow: `0 12px 30px -10px ${C.amber}88`,
            }}
          >
            Take the tour <ArrowUpRight size={13} />
          </div>
        </Link>

        {/* Client grid — sites launched this Summer */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {SHOWCASE.map((s) => (
            <ShowcaseCard key={s.slug} site={s} />
          ))}
        </div>

        {/* Small footer strip */}
        <div className="mt-14 flex items-center justify-center gap-3 text-xs uppercase tracking-[0.22em]" style={{ color: C.textFade }}>
          <Sparkles size={14} style={{ color: C.blueGlow }} />
          {SHOWCASE.length} sites launched this Summer · more in the pipeline
        </div>
      </div>
    </section>
  );
}

function ShowcaseCard({ site }) {
  // Concept-only cards (noLink=true) render as a non-clickable div so viewers
  // can see the design but can't navigate to the site — useful for pre-launch
  // client mock-ups and NDA'd builds.
  const Wrapper = site.noLink ? "div" : "a";
  const wrapperProps = site.noLink
    ? {}
    : { href: site.url, target: "_blank", rel: "noopener noreferrer" };
  return (
    <Wrapper
      {...wrapperProps}
      className="group relative rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1"
      style={{
        borderColor: site.justLaunched ? "#FFD700" : C.line,
        background: "rgba(17,24,39,0.55)",
        backdropFilter: "blur(10px)",
        boxShadow: site.justLaunched
          ? `0 40px 100px -30px rgba(255,215,0,0.45), 0 20px 60px -30px ${site.accent}66`
          : `0 40px 100px -40px ${site.accent}66`,
        cursor: site.noLink ? "default" : "pointer",
      }}
      data-testid={`showcase-card-${site.slug}`}
    >
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: C.line, background: "rgba(10,15,30,0.7)" }}>
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#ff5f57" }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#febc2e" }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#28c840" }} />
        <div className="ml-2 flex-1 px-2 py-0.5 rounded text-[10px] font-mono truncate"
             style={{ background: "rgba(255,255,255,0.06)", color: C.textDim }}>
          🔒 {site.conceptOnly ? "concept build · not public" : (site.url || "live-site").replace(/^https?:\/\//, "")}
        </div>
        <div
          className="flex items-center gap-1 text-[9px] uppercase tracking-[0.2em]"
          style={{ color: site.conceptOnly ? "#FFD700" : C.greenGlow }}
        >
          <span
            className="w-1 h-1 rounded-full"
            style={{
              background: site.conceptOnly ? "#FFD700" : C.greenGlow,
              boxShadow: `0 0 8px ${site.conceptOnly ? "#FFD700" : C.greenGlow}`,
            }}
          />
          {site.conceptOnly ? "Concept" : "Live"}
        </div>
      </div>

      {/* Screenshot */}
      <div className="relative aspect-[16/10] bg-black overflow-hidden">
        <img
          src={site.screenshotDesktop}
          alt={`${site.name} live homepage`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
          loading="lazy"
          data-testid={`showcase-screenshot-${site.slug}`}
        />
        {/* JUST LIVE launch ribbon (only shows for freshly-launched sites) */}
        {site.justLaunched && (
          <div
            className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-[0.22em]"
            style={{
              background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
              color: "#000",
              boxShadow: `0 10px 24px -6px rgba(255,215,0,0.6)`,
            }}
            data-testid={`showcase-ribbon-${site.slug}`}
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: "#000" }}
            />
            Just launched
          </div>
        )}
        {/* Launch date pill */}
        {site.justLaunched && site.launchedAt && (
          <div
            className="absolute top-3 right-3 z-10 text-[9px] font-mono px-2 py-1 rounded-md"
            style={{
              background: "rgba(0,0,0,0.65)",
              color: "#FFD700",
              border: "1px solid rgba(255,215,0,0.4)",
              backdropFilter: "blur(6px)",
            }}
          >
            {new Date(site.launchedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · Live
          </div>
        )}
        {/* Bottom fade so the card body reads clean */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-24"
          style={{ background: `linear-gradient(to top, rgba(17,24,39,0.9), transparent)` }}
        />
      </div>

      {/* Card body */}
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-bold text-lg text-white truncate">{site.name}</h3>
            <p className="mt-0.5 text-xs" style={{ color: C.textDim }}>
              {site.industry} · {site.location}
            </p>
          </div>
          <span
            className="shrink-0 inline-flex items-center gap-1 px-3 h-8 rounded-full text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{
              border: `1px solid ${site.accent}66`,
              color: site.accent,
              background: `${site.accent}12`,
            }}
          >
            {site.noLink ? <>Preview <Sparkles size={12} /></> : <>Visit <ArrowUpRight size={12} /></>}
          </span>
        </div>

        {/* Metric row */}
        <div className="mt-4 grid grid-cols-3 gap-2 pt-4 border-t" style={{ borderColor: C.line }}>
          {site.metrics.map((m) => (
            <div key={m.label} data-testid={`showcase-metric-${site.slug}-${m.label.toLowerCase().replace(/\s+/g,"-")}`}>
              <div className="text-sm md:text-base font-black text-white truncate">{m.value}</div>
              <div className="mt-0.5 text-[9px] uppercase tracking-[0.18em] truncate" style={{ color: C.textFade }}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>
    </Wrapper>
  );
}

// ────────── Pricing / SEO + Web Design offer ──────────
function Pricing() {
  const perks = [
    { icon: Globe,     title: "Website built end-to-end",   body: "10+ sections, mobile-optimized, hosted, and live in days — not weeks." },
    { icon: Search,    title: "SEO foundation from day one", body: "JSON-LD schema, sitemap, Open Graph, canonicals, hreflang — auto-injected on every page." },
    { icon: Radar,     title: "Lead-gen crawlers running",   body: "While your organic SEO ramps, our crawlers scan local directories, maps, and social to feed you qualified leads." },
    { icon: Bot,       title: "24/7 AI sales bot on-site",   body: "BotProviderAI qualifies visitors, answers questions, and books calls the moment they land." },
    { icon: TrendingUp, title: "Real revenue analytics",     body: "Not vanity metrics — actual pipeline, chat outcomes, and revenue attribution." },
    { icon: Rocket,    title: "Fully automated, hands-off",  body: "You focus on running the business. The platform runs itself, night and day." },
  ];

  return (
    <section id="pricing" className="relative z-10 py-24 md:py-32" data-testid="pricing-section">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-10 items-center">
          {/* Left: the price card */}
          <div
            className="relative rounded-3xl p-8 md:p-10 border overflow-hidden"
            style={{
              borderColor: `${C.blueBright}55`,
              background: `linear-gradient(160deg, rgba(139,92,246,0.20) 0%, rgba(252,211,77,0.10) 100%), ${C.inkSoft}`,
              boxShadow: `0 60px 160px -50px ${C.blueBright}77`,
            }}
            data-testid="pricing-card"
          >
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none opacity-30"
              style={{
                backgroundImage: `radial-gradient(500px 300px at 90% -10%, ${C.greenBright}55, transparent 60%),
                                  radial-gradient(500px 300px at -10% 110%, ${C.blueBright}55, transparent 60%)`,
              }}
            />
            <div className="relative">
              {/* Limited-time urgency ribbon */}
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-4 animate-pulse"
                style={{
                  background: `linear-gradient(135deg, ${C.greenBright} 0%, ${C.blueBright} 100%)`,
                  color: "white",
                  boxShadow: `0 8px 24px -8px ${C.greenBright}aa`,
                }}
                data-testid="pricing-urgency-badge"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-white" />
                Limited-time · Get it now while it lasts
              </div>

              <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: C.greenGlow }}>
                Web design + SEO
              </div>
              <div className="flex items-baseline gap-2 flex-wrap" data-testid="pricing-headline">
                <span
                  className="font-black tracking-[-0.02em] leading-none line-through opacity-40"
                  style={{ fontSize: "clamp(1.4rem, 2.6vw, 2.2rem)", color: C.textDim }}
                  aria-label="original price"
                >
                  $500
                </span>
                <span
                  className="font-black tracking-[-0.02em] leading-none"
                  style={{
                    fontSize: "clamp(3.5rem, 7vw, 6rem)",
                    backgroundImage: `linear-gradient(135deg, ${C.blueGlow} 0%, ${C.greenGlow} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  $299.99
                </span>
                <span
                  className="font-black leading-none"
                  style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", color: C.greenGlow }}
                  aria-label="asterisk"
                  data-testid="pricing-asterisk"
                >
                  *
                </span>
                <span className="text-lg md:text-xl font-semibold" style={{ color: C.textDim }}>
                  &nbsp;/ website
                </span>
              </div>

              <p className="mt-6 text-lg md:text-xl leading-relaxed text-white max-w-[520px]">
                Fully automated. <span style={{ color: C.greenGlow }}>Crawlers + AI bots</span>{" "}
                pull qualified leads in for you while your SEO is still building up.
              </p>

              <ul className="mt-6 space-y-2 text-sm" style={{ color: C.textDim }}>
                {[
                  "Website designed, built, hosted, and live in days",
                  "SEO foundation locked in from day one",
                  "24/7 AI sales bot answering visitors",
                  "Automated lead-gen while SEO ramps",
                ].map((x) => (
                  <li key={x} className="flex items-center gap-2">
                    <CheckCircle2 size={16} style={{ color: C.greenGlow }} /> {x}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 px-6 h-12 rounded-full font-semibold text-sm"
                  style={{
                    background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
                    color: "white",
                    boxShadow: `0 16px 40px -12px ${C.greenBright}88`,
                  }}
                  data-testid="pricing-cta"
                >
                  Book my $299.99 site <ArrowRight size={14} />
                </a>
                <a
                  href="#modules"
                  className="inline-flex items-center gap-2 px-5 h-12 rounded-full font-semibold text-sm border transition-all hover:bg-white/5"
                  style={{ borderColor: C.line, color: C.text }}
                >
                  See what&apos;s included <ArrowUpRight size={14} />
                </a>
              </div>

              <p className="mt-6 text-[11px] leading-relaxed" style={{ color: C.textFade }} data-testid="pricing-footnote">
                <span style={{ color: C.greenGlow }}>*</span> Starter build: up to 20 pages,
                hosted, mobile-optimized, SEO baseline, lead-capture forms, and one AI bot
                pre-trained on your business. Larger sites, extra modules (CRM, scheduling,
                billing, industry tools), and custom integrations priced by scope — quote
                given during the first call.
              </p>
            </div>
          </div>

          {/* Right: perks / positioning */}
          <div>
            <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: C.blueGlow }}>
              What $299.99 actually gets you
            </div>
            <h2 className="font-black tracking-[-0.02em] leading-[1.02] mb-8"
                style={{ fontSize: "clamp(1.9rem, 3.6vw, 3rem)" }}>
              A website that{" "}
              <span
                style={{
                  backgroundImage: `linear-gradient(135deg, ${C.blueGlow} 0%, ${C.greenGlow} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                sells for you
              </span>{" "}
              — not one that just sits there.
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {perks.map((p, i) => (
                <div
                  key={p.title}
                  className="rounded-2xl p-5 border"
                  style={{
                    borderColor: C.line,
                    background: "rgba(17,24,39,0.55)",
                    backdropFilter: "blur(10px)",
                  }}
                  data-testid={`pricing-perk-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                    style={{
                      background: `${i % 2 === 0 ? C.blueGlow : C.greenGlow}18`,
                      border: `1px solid ${i % 2 === 0 ? C.blueGlow : C.greenGlow}44`,
                    }}
                  >
                    <p.icon size={16} style={{ color: i % 2 === 0 ? C.blueGlow : C.greenGlow }} />
                  </div>
                  <div className="font-bold text-white">{p.title}</div>
                  <p className="mt-1.5 text-xs leading-relaxed" style={{ color: C.textDim }}>
                    {p.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────── Modules grid ──────────
function Modules() {
  return (
    <section id="modules" className="relative z-10 py-24 md:py-32" data-testid="modules-section">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="max-w-[820px] mb-16">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: C.blueGlow }}>
            One platform · Ten modules
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02]"
              style={{ fontSize: "clamp(2rem, 4.4vw, 3.8rem)" }}>
            Turn on only what{" "}
            <span
              style={{
                backgroundImage: `linear-gradient(135deg, ${C.blueGlow} 0%, ${C.greenGlow} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              your business
            </span>{" "}
            actually needs.
          </h2>
          <p className="mt-4 text-lg" style={{ color: C.textDim }}>
            Every module is native to {BRAND.platformName}. No integrations to babysit, no data
            silos, no Zapier tax. Add modules per tenant, per vertical, per client — instantly.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {MODULES.map((m, i) => (
            <ModuleCard key={m.title} {...m} accent={i % 2 === 0 ? C.blueGlow : C.greenGlow} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ModuleCard({ icon: Icon, title, body, accent }) {
  return (
    <div
      className="group relative rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-1"
      style={{
        borderColor: C.line,
        background: "rgba(17,24,39,0.55)",
        backdropFilter: "blur(10px)",
      }}
      data-testid={`module-card-${title.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}
    >
      <div
        aria-hidden
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{ boxShadow: `0 30px 80px -30px ${accent}66`, border: `1px solid ${accent}66` }}
      />
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
        style={{ background: `${accent}18`, border: `1px solid ${accent}44` }}
      >
        <Icon size={20} style={{ color: accent }} />
      </div>
      <h3 className="font-bold text-lg text-white">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed" style={{ color: C.textDim }}>{body}</p>
    </div>
  );
}

// ────────── How it works ──────────
function HowItWorks() {
  return (
    <section id="how" className="relative z-10 py-24 md:py-32 border-t" style={{ borderColor: C.line }} data-testid="how-section">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="max-w-[820px] mb-16">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: C.greenGlow }}>
            How it works
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02]"
              style={{ fontSize: "clamp(2rem, 4.4vw, 3.8rem)" }}>
            Three steps. No SaaS graveyard.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {HOW.map((h, i) => (
            <div
              key={h.n}
              className="rounded-2xl p-8 border relative overflow-hidden"
              style={{
                borderColor: C.line,
                background: `linear-gradient(180deg, rgba(17,24,39,0.7) 0%, rgba(17,24,39,0.35) 100%)`,
              }}
              data-testid={`how-step-${h.n}`}
            >
              <div
                className="font-black tracking-tight leading-none mb-6"
                style={{
                  fontSize: "clamp(3rem, 5vw, 4rem)",
                  backgroundImage: `linear-gradient(135deg, ${i % 2 === 0 ? C.blueGlow : C.greenGlow} 0%, ${C.textFade} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {h.n}
              </div>
              <h3 className="text-xl font-bold text-white">{h.title}</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: C.textDim }}>{h.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ────────── Final CTA ──────────
function FinalCTA() {
  return (
    <section
      id="contact"
      className="relative z-10 py-28 md:py-36"
      data-testid="final-cta-section"
    >
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <div
          className="relative rounded-3xl overflow-hidden p-10 md:p-16 border"
          style={{
            borderColor: `${C.blueBright}55`,
            background: `linear-gradient(135deg, rgba(139,92,246,0.22) 0%, rgba(252,211,77,0.14) 100%), ${C.inkSoft}`,
            boxShadow: `0 80px 200px -60px ${C.blueBright}66`,
          }}
        >
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{
              backgroundImage: `radial-gradient(600px 300px at 80% -10%, ${C.greenBright}44, transparent 60%),
                                radial-gradient(600px 300px at 10% 110%, ${C.blueBright}44, transparent 60%)`,
            }}
          />
          <div className="relative flex flex-col md:flex-row md:items-center gap-10">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: C.greenGlow }}>
                Ready to plug it in?
              </div>
              <h2 className="font-black tracking-[-0.02em] leading-[1.02] text-white"
                  style={{ fontSize: "clamp(2rem, 4.2vw, 3.4rem)" }}>
                Launch your next business on {BRAND.platformName}.
              </h2>
              <p className="mt-4 max-w-[600px] text-lg" style={{ color: C.textDim }}>
                Sign up today and within 3 days you&apos;ll be done — and we&apos;ll be going over your project together.
              </p>
              <ul className="mt-6 space-y-2 text-sm" style={{ color: C.textDim }}>
                {["No Zapier tax","No SaaS graveyard","One database, one login","Yours forever"].map((x) => (
                  <li key={x} className="flex items-center gap-2">
                    <CheckCircle2 size={16} style={{ color: C.greenGlow }} /> {x}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-3 md:min-w-[300px]">
              <a
                href="tel:+12148155966"
                className="inline-flex items-center justify-center gap-2 px-6 h-14 rounded-full font-semibold text-base"
                style={{
                  background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
                  color: "white",
                  boxShadow: `0 20px 60px -20px ${C.greenBright}88`,
                }}
                data-testid="final-cta-call"
              >
                Call (214) 815-5966 <ArrowRight size={16} />
              </a>
              <a
                href="mailto:cgarciathanksyou@gmail.com?subject=Book%20a%20build"
                className="inline-flex items-center justify-center gap-2 px-6 h-14 rounded-full font-semibold text-base border transition-all hover:bg-white/5"
                style={{ borderColor: `${C.blueBright}66`, color: C.text }}
                data-testid="final-cta-book"
              >
                Email me <ArrowRight size={16} />
              </a>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 px-4 h-11 rounded-full font-semibold text-sm border transition-all hover:bg-white/5"
                style={{ borderColor: C.line, color: C.textDim }}
                data-testid="final-cta-signin"
              >
                Sign in to platform
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────── Footer ──────────
function Footer() {
  return (
    <footer
      className="relative z-10 border-t"
      style={{ borderColor: C.line, background: "rgba(10,15,30,0.85)" }}
      data-testid="plugitai-footer"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-11 w-auto" />
            <div className="text-[11px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
              Powered by {BRAND.engineName} · {BRAND.botName} inside
            </div>
          </div>
          <p className="text-sm max-w-[440px]" style={{ color: C.textDim }}>
            {BRAND.platformTagline} Built in Texas. Multi-tenant, multi-lingual, and yours forever.
          </p>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: C.textFade }}>Platform</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#modules"  className="hover:text-white" style={{ color: C.textDim }}>Modules</a></li>
            <li><a href="#showcase" className="hover:text-white" style={{ color: C.textDim }}>Showcase</a></li>
            <li><Link to="/about"   className="hover:text-white" style={{ color: C.textDim }}>About Kit</Link></li>
            <li><Link to="/tx"      className="hover:text-white" style={{ color: C.textDim }}>DFW cities</Link></li>
            <li><Link to="/learn"   className="hover:text-white" style={{ color: C.textDim }}>Learn</Link></li>
            <li><Link to="/blog"    className="hover:text-white" style={{ color: C.textDim }}>Blog</Link></li>
            <li><Link to="/case-studies/miss-rodriguez" className="hover:text-white" style={{ color: C.textDim }}>Case study: Miss Rodriguez</Link></li>
            <li><Link to="/login"   className="hover:text-white" style={{ color: C.textDim }}>Sign in</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: C.textFade }}>Contact</div>
          <ul className="space-y-2 text-sm">
            <li><a href="tel:+12148155966" className="hover:text-white" style={{ color: C.textDim }} data-testid="footer-phone">(214) 815-5966</a></li>
            <li><a href="mailto:cgarciathanksyou@gmail.com" className="hover:text-white" style={{ color: C.textDim }} data-testid="footer-email">Email Me</a></li>
            <li><a href="#contact" className="hover:text-white" style={{ color: C.textDim }}>Book a build</a></li>
          </ul>
        </div>
      </div>

      {/* Cities strip — DFW SEO surface area */}
      <div className="border-t" style={{ borderColor: C.line }}>
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-8" data-testid="footer-cities-strip">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-[0.22em]" style={{ color: C.textFade }}>
              Serving DFW · <span style={{ color: "rgba(255,255,255,0.75)" }}>{DFW_CITIES.length} cities</span>
            </div>
            <Link to="/tx" className="text-[11px] uppercase tracking-[0.22em] font-black hover:text-white" style={{ color: C.textDim }} data-testid="footer-cities-all">
              View all →
            </Link>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-[12px]">
            {DFW_CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/tx/${c.slug}`}
                className="hover:text-white transition-colors"
                style={{ color: C.textDim }}
                data-testid={`footer-city-${c.slug}`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t" style={{ borderColor: C.line }}>
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs" style={{ color: C.textFade }}>
          <div>{BRAND.legalFooter}</div>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <a
              href="https://www.plugitai.com"
              className="uppercase tracking-[0.22em] hover:text-white transition-colors"
              style={{ color: C.textDim }}
              data-testid="footer-built-by-kit"
            >
              100% built by Kit
            </a>
            <span aria-hidden="true">·</span>
            <Link
              to="/about"
              className="uppercase tracking-[0.22em] hover:text-white transition-colors"
              style={{ color: C.textDim }}
              data-testid="footer-practically-built-by"
            >
              practically built by
            </Link>
            <span aria-hidden="true">·</span>
            <div className="flex items-center gap-2">
              <Layers size={12} style={{ color: C.blueGlow }} />
              <span className="uppercase tracking-[0.22em]">{BRAND.footerCredit}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
